type GAEvent = {
  event: string;
  category: string;
  action: string;
  label: string;
  // new fields
  widget_version: string;
  step_name: string;
  previous_step_name?: string;
  step_number: number;
} & Record<string, unknown>;

export type Events = typeof events;

const getStepEventCreator = (
  stepName:
    | 'step-viewed'
    | 'step-submitted'
    | 'step-skipped'
    | 'step-closed'
    | 'step-reset'
    | 'step-back'
    | 'step-click_on_logo'
    | 'step-click_to_close_widget',
) => {
  return ({
    stepId,
    prevStepId,
    widgetId,
    stepIndex,
    transactionId,
    ...custom
  }: {
    stepId: string;
    prevStepId?: string;
    widgetId: string;
    stepIndex: number;
    transactionId: string;
  } & Record<string, unknown>): GAEvent => ({
    ...custom,
    event: 'custom-event',
    category: 'widget-step',
    action: stepName.split('-')[1] as string,
    label: `${widgetId}--${stepId}--${stepIndex}`,
    // new fields
    widget_version: widgetId,
    step_name: stepId,
    previous_step_name: prevStepId,
    step_number: stepIndex,
    transaction_id: transactionId,
  });
};

const getWidgetEventCreator = (
  stepName:
    | 'widget-started'
    | 'widget-submitted'
    | 'widget-reset'
    | 'widget-closed'
    | 'widget-submitFailed',
) => {
  return ({ widgetId }: { widgetId: string }) => ({
    event: 'custom-event',
    category: 'widget',
    action: `${stepName.split('-')[1] as string}`,
    label: `${widgetId}`,
  });
};

const events = {
  'step-viewed': getStepEventCreator('step-viewed'),
  'step-submitted': getStepEventCreator('step-submitted'),
  'step-skipped': getStepEventCreator('step-skipped'),
  'step-back': getStepEventCreator('step-back'),
  'step-closed': getStepEventCreator('step-closed'),
  'step-reset': getStepEventCreator('step-reset'),
  'step-click_on_logo': getStepEventCreator('step-click_on_logo'),
  'step-click_to_close_widget': getStepEventCreator('step-click_to_close_widget'),
  'widget-started': getWidgetEventCreator('widget-started'),
  'widget-submitted': getWidgetEventCreator('widget-submitted'),
  'widget-submitFailed': getWidgetEventCreator('widget-submitFailed'),
  'widget-reset': getWidgetEventCreator('widget-reset'),
  'widget-closed': getWidgetEventCreator('widget-closed'),
  'custom-event': (fields: Record<string, unknown>) => ({ ...fields, event: 'custom-event' }),
  // to emit button_click event use 'click-event', info = button_group, label = buttonText.
  'click-event': ({
    buttonGroup,
    buttonText,
    buttonUrl,
  }: {
    buttonGroup: string;
    buttonText: string;
    buttonUrl?: string;
  }) => ({
    event: 'click_event',
    event_category: 'click',
    button_group: buttonGroup,
    button_text: buttonText,
    button_href: buttonUrl,
    ...(buttonUrl && { button_url: buttonUrl }),
  }),
  outbound_click: ({ info, label }: { info: string; label: string }) => ({
    event: 'outbound_click',
    info,
    label,
  }),
  'open-accordion': ({ label, name }: { label: string; name?: string }) => ({
    event: 'open-accordion',
    label,
    ...(name && { name }),
  }),
  navigation_click: ({ label, info }: { label: string; info: string }) => ({
    event: 'navigation_click',
    label,
    info,
  }),
  subnavigation_click: ({ label, info }: { label: string; info: string }) => ({
    event: 'subnavigation_click',
    label,
    info,
  }),
  form_submit_success: ({ label, info }: { label: string; info: string }) => ({
    event: 'form_submit_success',
    label,
    info,
  }),
} as const;

export function pushGAEvent<T extends keyof Events>(
  eventName: T,
  params: Parameters<Events[T]>[0],
): void {
  const eventParams = events[eventName](params as any);

  const commonParams = {
    site_area: window?.location.pathname,
  };

  window.dataLayer?.push({ ...eventParams, ...commonParams });
}
